import React from 'react';
import DOMPurify from 'dompurify';
import { Clock, Users, ChefHat, BookOpen, Calendar, User } from 'lucide-react';
import type { RmContentRecipe } from '../../types/rmContent';
import { buildVimeoEmbedUrl } from '../../utils/urlUtils';
import RmContentFooter from './RmContentFooter';

interface RmRecipeViewerProps {
  content: RmContentRecipe;
}

const formatDate = (dateStr: string): string => {
  try {
    return new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric', year: 'numeric' }).format(new Date(dateStr));
  } catch {
    return '';
  }
};

const RmRecipeViewer: React.FC<RmRecipeViewerProps> = ({ content }) => {
  const teacherNames = content.teacher
    ?.map((t: any) => (typeof t === 'string' ? t : t.title || t.slug || ''))
    .filter(Boolean);

  return (
    <div>
      {/* Hero: Vimeo → Image → Navy banner */}
      {content.vimeoId ? (
        <div className="w-full bg-gray-950 px-4 py-6 sm:px-6 sm:py-8">
          <div className="max-w-4xl mx-auto aspect-video rounded-xl overflow-hidden shadow-2xl">
            <iframe
              src={buildVimeoEmbedUrl(content.vimeoId)}
              className="w-full h-full"
              frameBorder="0"
              allow="autoplay; fullscreen; picture-in-picture; clipboard-write; encrypted-media"
              referrerPolicy="strict-origin-when-cross-origin"
              title={content.title || 'Recipe Video'}
              loading="lazy"
            />
          </div>
        </div>
      ) : content.image?.src ? (
        <div className="relative w-full h-56 sm:h-72 md:h-80 overflow-hidden">
          <img
            src={content.image.src}
            alt={content.title || ''}
            className="w-full h-full object-cover"
            loading="lazy"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
          <div className="absolute bottom-0 left-0 right-0 p-6">
            <div className="max-w-3xl mx-auto">
              <h1 className="text-xl sm:text-2xl md:text-3xl font-bold text-white leading-tight drop-shadow-lg">
                {content.title}
              </h1>
            </div>
          </div>
        </div>
      ) : (
        <div className="bg-rm-primary-700 px-6 py-8">
          <div className="max-w-3xl mx-auto">
            <h1 className="text-xl sm:text-2xl font-bold text-white">{content.title}</h1>
            <div className="w-12 h-0.5 bg-rm-accent-500 rounded-full mt-3" />
          </div>
        </div>
      )}

      {/* Stats & meta bar */}
      <div className="max-w-3xl mx-auto px-6 pt-5">
        <div className="flex flex-wrap items-center gap-3">
          {content.cookingTime > 0 && (
            <div className="inline-flex items-center gap-2 bg-rm-bg-50 rounded-xl px-4 py-2.5 shadow-soft text-sm font-medium text-rm-ink-900">
              <Clock className="w-4 h-4 text-rm-accent-500" />
              {content.cookingTime} min
            </div>
          )}
          {content.servings > 0 && (
            <div className="inline-flex items-center gap-2 bg-rm-bg-50 rounded-xl px-4 py-2.5 shadow-soft text-sm font-medium text-rm-ink-900">
              <Users className="w-4 h-4 text-rm-accent-500" />
              {content.servings} servings
            </div>
          )}
          <div className="flex-1" />
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-rm-neutral-600">
            {content.publishedAt && (
              <span className="inline-flex items-center gap-1.5">
                <Calendar className="w-3.5 h-3.5" />
                {formatDate(content.publishedAt)}
              </span>
            )}
            {teacherNames?.length > 0 && (
              <span className="inline-flex items-center gap-1.5">
                <User className="w-3.5 h-3.5" />
                {teacherNames.join(', ')}
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Intro */}
      {content.intro && (
        <div className="max-w-3xl mx-auto px-6 pt-5">
          <div
            className="prose prose-lg max-w-none text-gray-700"
            dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(content.intro) }}
          />
        </div>
      )}

      {/* Two-column: Preparation + Ingredients sidebar */}
      <div className="max-w-3xl mx-auto px-6 py-6">
        <div className="lg:grid lg:grid-cols-[1fr_280px] lg:gap-8">
          {/* Ingredients sidebar — mobile first, desktop right */}
          {content.ingredients && (
            <div className="lg:order-2 mb-6 lg:mb-0">
              <div className="lg:sticky lg:top-4 bg-rm-bg-50 rounded-xl p-5 shadow-soft">
                <div className="flex items-center gap-2 mb-3">
                  <BookOpen className="w-4 h-4 text-rm-accent-500" />
                  <h2 className="text-base font-semibold text-rm-ink-900">Ingredients</h2>
                </div>
                <div
                  className="prose prose-sm max-w-none text-gray-700 [&_ul]:space-y-1 [&_li]:leading-relaxed"
                  dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(content.ingredients) }}
                />
              </div>
            </div>
          )}

          {/* Preparation — main column */}
          {content.description && (
            <div className="lg:order-1">
              <div className="flex items-center gap-2 mb-3">
                <ChefHat className="w-4 h-4 text-rm-accent-500" />
                <h2 className="text-base font-semibold text-rm-ink-900">Preparation</h2>
              </div>
              <div
                className="prose prose-base max-w-none text-gray-700"
                dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(content.description) }}
              />
            </div>
          )}
        </div>

        <RmContentFooter pillar={content.pillar} teacher={content.teacher} tags={content.tags} />
      </div>
    </div>
  );
};

export default RmRecipeViewer;
